/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IComponentProps } from '@msdyn365-commerce/core';
import { CommerceException } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Alert, Button } from '@msdyn365-commerce-modules/utilities';
import React from 'react';

import { moduleClassName } from '../dlvry-business-partners';
import { IDlvryBusinessPartnersResources } from '../dlvry-business-partners.props.autogenerated';
import { SearchBar } from './search-bar';
import { format } from '@msdyn365-commerce/retail-proxy';

type PageItem = number | "ellipsis";
interface PaginationOptions {
  /** Pages shown on each side of the current page. Default 2. */
  siblingCount?: number;
  /** Pages always shown at the very start and end. Default 1. */
  boundaryCount?: number;
  /** Total slots in the bar. Defaults to the width needed to honor the
   *  sibling/boundary counts: boundary*2 + sibling*2 + 3 (current + 2 ellipses). */
  size?: number;
}

/**
 * Props of DataGrid component.
 */
export interface IDataGridProps<Tdata> extends IComponentProps {
    className: string;
    pageSize?: number;
    searchPlaceholderLabel: string;
    defaultSorting?: ISortingInfo;
    resources: IDlvryBusinessPartnersResources;
    columns: IDataGridColumn<Tdata>[];
    loadData(): Promise<Tdata[]>;
}

/**
 * State of DataGrid component.
 */
export interface IDataGridState<Tdata> {
    data: Tdata[];
    paging: IPagingInfo;
    filter: IFilterInfo;
    sorting?: ISortingInfo;
    isLoading: boolean;
    errorMessage: string;
}

export interface IPagingInfo {
    index: number;
}

export interface IFilterInfo {
    searchText: string;
}

export interface ISortingInfo {
    fieldName: string;
    ascending: boolean;
}

/**
 * Column definition of DataGrid component.
 */
export interface IDataGridColumn<Tdata> {
    /**
     * A unique key for identifying the column.
     */
    reactKey: string;

    /**
     * Name to render on the column header.
     */
    name: string;

    /**
     * Class name added to table cell.
     */
    className?: string;

    /**
     * The field to pull the text value from for the column.Can be unset if a custom `onRender` method is provided.
     */
    fieldName?: string;

    /**
     * Whether the user can search this column by text.
     */
    isSearchable?: boolean;

    /**
     * Custom renderer for cell content, instead of the default text rendering.
     */
    onRender?(item: Tdata): JSX.Element;

    /**
     * Custom renderer for header cell content, instead of the default text rendering.
     */
    onHeaderRender?(column: IDataGridColumn<Tdata>): JSX.Element;

    /**
     * Event when the table cell is clicked.
     */
    onClick?(item: Tdata): void;
}

const defaultPageSize = 10;

/**
 * DataGrid component encapsulates common table functionalities.
 */
export class DataGrid<Tdata> extends React.Component<IDataGridProps<Tdata>, IDataGridState<Tdata>> {
    constructor(props: IDataGridProps<Tdata>) {
        super(props);
        this.state = {
            data: [],
            sorting: props.defaultSorting,
            paging: { index: 0 },
            filter: { searchText: '' },
            isLoading: true,
            errorMessage: ''
        };
    }

    public componentDidMount(): void {
        void this.loadDataInternal();
    }

    public shouldComponentUpdate(nextProps: Readonly<IDataGridProps<Tdata>>, nextState: Readonly<IDataGridState<Tdata>>): boolean {
        return (
            this.props.id !== nextProps.id ||
            this.props.columns !== nextProps.columns ||
            this.state.data !== nextState.data ||
            this.state.paging !== nextState.paging ||
            this.state.filter !== nextState.filter ||
            this.state.isLoading !== nextState.isLoading ||
            this.state.errorMessage !== nextState.errorMessage
        );
    }

    public render(): JSX.Element | null {
        const data = this.getDataToDisplay();
        if (this.state.errorMessage) {
            return (
                <>
                    <SearchBar {...this.props} disabled onSearch={this.onSearch} />
                    <table className={this.props.className}>
                        {this.renderGridHeader()}
                    </table>
                    <Alert color='danger'>{this.state.errorMessage}</Alert>
                </>
            );
        }
        if (this.state.isLoading) {
            return (
                <>
                    <SearchBar {...this.props} disabled onSearch={this.onSearch} />
                    <table className={this.props.className}>
                        {this.renderGridHeader()}
                    </table>
                    <span className={`${moduleClassName}__loading`}>{this.props.resources.loadingLabel}</span>
                </>
            );
        }
        if (!ArrayExtensions.hasElements(data)) {
            return (
                <>
                    <SearchBar {...this.props} onSearch={this.onSearch} />
                    <table className={this.props.className}>
                        {this.renderGridHeader()}
                    </table>
                    <p className={`${moduleClassName}__empty-message`}>{this.props.resources.noDataMessage}</p>
                </>
            );
        }
        return (
            <>
                <SearchBar {...this.props} onSearch={this.onSearch} />
                <table className={this.props.className}>
                    {this.renderGridHeader()}
                    {data.map((item, index) => this.renderGridRow(item, index))}
                </table>
                {this.renderPaginator()}
            </>
        );
    }

    /**
     * Renders the TH header in the table.
     * @returns - Header react node.
     */
    private readonly renderGridHeader = () => {
        const className = `${this.props.className}__header`;
        const rowClassName = `${className}__row`;
        return (
            <thead className={className}>
                <tr className={rowClassName}>
                    {this.props.columns.map(column => (
                        <th key={column.reactKey} className={column.className}>
                            {column.onHeaderRender ? column.onHeaderRender(column) : column.name}
                        </th>
                    ))}
                </tr>
            </thead>
        );
    };

    /**
     * Renders table row.
     * @param item - Data item for this row.
     * @param index - Index of the row.
     * @returns - Row react node.
     */
    private readonly renderGridRow = (item: Tdata, index: number) => {
        const rowClassName = `${this.props.className}__row`;
        return (
            <tr className={rowClassName} key={index}>
                {this.props.columns.map(column => {
                    return (
                        <td key={column.reactKey} className={column.className} onClick={() => column.onClick?.(item)}>
                            {column.onRender ? (
                                column.onRender(item)
                            ) : (
                                <span>{((item as unknown) as { [key: string]: string })[column.fieldName || '']}</span>
                            )}
                        </td>
                    );
                })}
            </tr>
        );
    };

    /**
     * Load data for the table.
     * @returns Promise<void>.
     */
    private readonly loadDataInternal = async () => {
        if (!this.props.context.actionContext.requestContext.user.isAuthenticated) {
            this.setState({
                isLoading: false
            });
            return;
        }
        try {
            const result = await this.props.loadData();
            this.setState({
                isLoading: false,
                data: result
            });
        } catch (e) {
            this.setState({
                isLoading: false,
                errorMessage: (e as { data: CommerceException })?.data?.LocalizedMessage || this.props.resources.genericErrorMessage
            });
        }
    };

    private readonly onSearch = (searchText: string): void => {
        this.setState({
            paging: { index: 0 },
            filter: { searchText }
        });
    };

    private readonly sortData = (): Tdata[] => {
        const sorting = this.state.sorting;
        if (!sorting) {
            return this.state.data;
        }
        const { fieldName, ascending } = sorting;
        return this.state.data.sort((item1: Tdata, item2: Tdata) => {
            let value1 = ((item1 as unknown) as { [key: string]: string })[fieldName];
            let value2 = ((item2 as unknown) as { [key: string]: string })[fieldName];
            if (typeof value1 === 'string' && typeof value2 === 'string') {
                value1 = value1.toLowerCase();
                value2 = value2.toLowerCase();
            }
            const compareResult = value1 > value2 ? 1 : value1 < value2 ? -1 : 0;
            return ascending ? compareResult : compareResult * -1;
        });
    };

    private readonly filterData = (): Tdata[] => {
        const columns = this.props.columns.filter(c => c.isSearchable);
        const searchText = this.state.filter.searchText;
        return this.sortData().filter(item => {
            if (!columns || columns.length === 0 || !searchText) {
                return true;
            }
            return columns.some(column => {
                const columnValue = ((item as unknown) as { [key: string]: string })[column.fieldName!];
                return columnValue && columnValue.toLowerCase().indexOf(searchText.trim().toLowerCase()) >= 0;
            });
        });
    };

    private readonly getDataToDisplay = (): Tdata[] => {
        const skip = this.state.paging.index * (this.props.pageSize || defaultPageSize);
        const top = this.props.pageSize || defaultPageSize;
        const filteredItems = this.filterData();
        return filteredItems.slice(skip, skip + top);
    };

    private readonly getPaginationItems = (
        current: number,   // 1-based current page
        total: number,     // total page count
        opts: PaginationOptions = {}
    ): PageItem[] => {
        const around = opts.siblingCount ?? 2;
        const boundary = opts.boundaryCount ?? 1;
        const size = opts.size ?? boundary * 2 + around * 2 + 3;

        if (total <= size) {
            return Array.from({ length: total }, (_, i) => i + 1);
        }

        const first = 1;
        const last = total;

        let lo = current - around;
        let hi = current + around;

        // Show an ellipsis only when it hides 2+ pages; a single hidden page
        // is rendered as a number instead.
        const leftEllipsis = lo > first + boundary + 1;
        const rightEllipsis = hi < last - boundary - 1;

        // A cap (boundary pages + its ellipsis) consumes boundary+1 slots;
        // when absent, those slots are absorbed by the sliding run so the
        // overall width stays exactly `size`.
        const leftCap = leftEllipsis ? boundary + 1 : 0;
        const rightCap = rightEllipsis ? boundary + 1 : 0;
        const runLen = size - leftCap - rightCap;

        if (leftEllipsis && rightEllipsis) {
            lo = current - Math.floor((runLen - 1) / 2);
            hi = lo + runLen - 1;
        } else if (!leftEllipsis && rightEllipsis) {
            lo = first;
            hi = lo + runLen - 1;
        } else if (leftEllipsis && !rightEllipsis) {
            hi = last;
            lo = hi - runLen + 1;
        } else {
            lo = first;
            hi = last;
        }

        const items: PageItem[] = [];
        if (leftEllipsis) {
            for (let p = first; p <= boundary; p++) items.push(p);
            items.push("ellipsis");
        }
        for (let p = lo; p <= hi; p++) items.push(p);
        if (rightEllipsis) {
            items.push("ellipsis");
            for (let p = last - boundary + 1; p <= last; p++) items.push(p);
        }

        return items;
    };


    private readonly renderPaginator = (): JSX.Element | null => {
        const { paging } = this.state;
        const itemsPerPage = this.props.pageSize || defaultPageSize;
        const totalItems = this.filterData().length;
        const startItem = paging.index * itemsPerPage + 1;
        const endItem = Math.min(startItem + itemsPerPage - 1, totalItems);

        const totalPages = Math.ceil(totalItems / itemsPerPage);

        return (
            <div className={`${this.props.className}__paginator`}>
                <span dangerouslySetInnerHTML={{ __html:
                    format(this.props.resources.businessPartnerDataGridPaginatorStatus, startItem, endItem, totalItems)
                }} />
                <div className={`${this.props.className}__paginator-buttons`}>
                    <Button
                        className={`${this.props.className}__paginator-button previous`}
                        disabled={paging.index === 0}
                        onClick={() => this.setState({ paging: { index: Math.max(paging.index - 1, 0) } })}
                        aria-label={this.props.resources.businessPartnerDataGridPaginatorPreviousButtonLabel}
                    />
                    {this.getPaginationItems(paging.index + 1, totalPages, { siblingCount: 1 }).map((item, index) => {
                        if (item === 'ellipsis') {
                            return <span key={index} className={`${this.props.className}__paginator-ellipsis`}>...</span>;
                        }
                        return (
                            <Button
                                key={index}
                                onClick={() => this.setState({ paging: { index: item - 1 } })}
                                className={`${this.props.className}__paginator-button page-number ${paging.index === item - 1 ? 'active' : ''}`}
                            >
                                {item}
                            </Button>
                        );
                    })}
                    <Button
                        className={`${this.props.className}__paginator-button next`}
                        disabled={paging.index >= totalPages - 1}
                        onClick={() => this.setState({ paging: { index: Math.min(paging.index + 1, totalPages - 1) } })}
                        aria-label={this.props.resources.businessPartnerDataGridPaginatorNextButtonLabel}
                    />
                </div>
            </div>
        );
    };
}
